// The Security page's operator "Export report" (Hugo Andrade, 2026-10-21, ENTRAID-347). The customer's admin creates
// the server-side Security posture report (Reports); an IPC Global operator account cannot (the customer's report
// function never builds or releases content for an operator), so it downloads this client-side summary of the posture
// it already sees on the Security page. Nothing is fetched or stored: the file is built from the page's own data.
//
// Every value from the run is HTML-escaped or reduced to a finite number; colors and labels come from fixed maps
// only. CIS wording is protective (readiness indication only, never certification or endorsement).
import { refLabel, refList } from './control-refs.js';
import { CIS_FRAMEWORK_ID, CIS_NOTICE } from '../../../shared/compliance/engine/notices.mjs';
import { formatPoints, formatScore } from './score-format.js';

const escapeHtml = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const numText = (v, fallback = '--') => (num(v) === null ? fallback : String(num(v)));

export const EXPORT_DISCLAIMER = 'Configuration summary from the latest onGuard collection, for review only. Not a certification or an audit opinion.';

const STATUS_ICON = Object.freeze({ pass: 'P', warn: '!', fail: 'X' });
const STATUS_COLOR = Object.freeze({ pass: '#047857', warn: '#b45309', fail: '#dc2626' });
const SEVERITY_COLOR = Object.freeze({ critical: '#b91c1c', high: '#c2410c', medium: '#b45309', low: '#1d4ed8', info: '#475569' });
const CHANGE_COLOR = Object.freeze({ new: '#1d4ed8', pass: '#047857', warn: '#b45309', fail: '#dc2626' });
const CATEGORY_NAMES = Object.freeze({
  identity: 'Identity and accounts', authentication: 'Authentication', accessControl: 'Access control',
  applications: 'Applications and permissions', monitoring: 'Monitoring and risk',
});
const pick = (map, key, fallback) => (Object.prototype.hasOwnProperty.call(map, key) ? map[key] : fallback);

function controlRefsHtml(refs) {
  const part = (label, list) => (list.length ? `<div><strong>${label}:</strong> ${list.map((r) => escapeHtml(refLabel(r))).join(', ')}</div>` : '');
  return part('Evaluates', refList(refs?.evaluates)) + part('Informs', refList(refs?.informs));
}

const citesCis = (findings) => findings.some((f) => ['evaluates', 'informs'].some((k) => refList(f?.controlRefs?.[k]).some((r) => r.framework === CIS_FRAMEWORK_ID)));

/** The export document as a string (pure; exportSecurityReport downloads it). */
export function securityExportHtml(posture, { timestamp = null, companyName = '', now = new Date() } = {}) {
  const findings = Array.isArray(posture?.findings) ? posture.findings : [];
  const categories = posture?.categories && typeof posture.categories === 'object' ? posture.categories : {};
  const fmt = (d) => d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  const collected = timestamp && Number.isFinite(Date.parse(timestamp)) ? fmt(new Date(timestamp)) : 'unknown date';
  const order = { fail: 0, warn: 1, pass: 2 };
  const sevOrder = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };

  const rows = findings
    .filter((f) => f && Object.prototype.hasOwnProperty.call(STATUS_ICON, f.status))
    .sort((a, b) => (order[a.status] - order[b.status]) || ((sevOrder[a.severity] ?? 5) - (sevOrder[b.severity] ?? 5)))
    .map((f) => `<tr>
<td><span class="dot" style="background:${STATUS_COLOR[f.status]}">${STATUS_ICON[f.status]}</span></td>
<td class="mono">${escapeHtml(f.id)}</td>
<td><strong>${escapeHtml(f.title)}</strong>${f.remediation && f.status !== 'pass' ? `<div class="fix">${escapeHtml(f.remediation)}</div>` : ''}</td>
<td><span class="sev" style="background:${pick(SEVERITY_COLOR, f.severity, '#475569')}">${escapeHtml(f.severity)}</span></td>
<td class="refs">${controlRefsHtml(f.controlRefs)}</td>
</tr>`).join('\n');

  const cats = Object.entries(categories).map(([id, c]) => `<div class="cat"><div class="cat-name">${escapeHtml(pick(CATEGORY_NAMES, id, id))}</div><div class="cat-score">${numText(c?.score)}</div><div class="cat-counts">${numText(c?.pass, '0')} passed, ${numText(c?.warn, '0')} warnings, ${numText(c?.fail, '0')} failed</div></div>`).join('');

  const changes = Array.isArray(posture?.drift?.changes) ? posture.drift.changes.filter(Boolean) : [];
  const delta = num(posture?.drift?.scoreDelta);
  const changeChip = (s) => `<span class="chip" style="color:${pick(CHANGE_COLOR, s, '#475569')}">${escapeHtml(s)}</span>`;
  const driftHtml = changes.length ? `<h2>Changes since the previous collection${delta ? ` (onGuard score ${delta > 0 ? 'up' : 'down'} ${formatPoints(delta)})` : ''}</h2>
<table><thead><tr><th>Check</th><th>Title</th><th>Previous</th><th>Current</th></tr></thead><tbody>
${changes.map((c) => `<tr><td class="mono">${escapeHtml(c.id)}</td><td>${escapeHtml(c.title)}</td><td>${changeChip(c.from)}</td><td>${changeChip(c.to)}</td></tr>`).join('\n')}
</tbody></table>` : '';

  const footer = [EXPORT_DISCLAIMER, citesCis(findings) ? CIS_NOTICE : null].filter(Boolean).map((t) => `<p>${escapeHtml(t)}</p>`).join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Security summary</title>
<style>
body{font-family:Inter,system-ui,-apple-system,sans-serif;margin:0;background:#f8fafc;color:#1e293b}
.wrap{max-width:900px;margin:0 auto;padding:32px 20px}
.head{background:#002f67;color:#fff;padding:24px 28px;border-radius:10px;margin-bottom:24px}
.head h1{margin:0 0 4px;font-size:22px}.head p{margin:0;opacity:.8;font-size:13px}
.score{background:#fff;border:1px solid #e2e8f0;border-radius:10px;padding:20px;margin-bottom:20px;font-size:14px}
.score strong{font-size:28px}
.cats{display:flex;flex-wrap:wrap;gap:10px;margin-bottom:20px}
.cat{flex:1;min-width:140px;background:#fff;border:1px solid #e2e8f0;border-radius:8px;padding:12px}
.cat-name{font-size:11px;text-transform:uppercase;color:#64748b;font-weight:600}.cat-score{font-size:24px;font-weight:700}.cat-counts{font-size:11px;color:#64748b}
h2{font-size:14px;color:#334155;margin:20px 0 8px}
table{width:100%;border-collapse:collapse;background:#fff;border:1px solid #e2e8f0}
th{background:#f1f5f9;text-align:left;padding:8px;font-size:11px;color:#475569}td{padding:8px;border-top:1px solid #e2e8f0;font-size:12px;vertical-align:top}
.mono{font-family:monospace;color:#475569}.fix{color:#1d4ed8;font-size:11px;margin-top:2px}.refs{font-size:11px;color:#475569}
.dot{display:inline-block;width:18px;height:18px;border-radius:2px;color:#fff;text-align:center;line-height:18px;font-size:10px;font-weight:700}
.sev{display:inline-block;padding:1px 8px;border-radius:9px;color:#fff;font-size:10px;font-weight:600}.chip{font-weight:600}
.foot{margin-top:28px;font-size:11px;color:#475569;border-top:1px solid #e2e8f0;padding-top:12px}
@media print{body{background:#fff}.wrap{padding:0}}
</style>
</head>
<body>
<div class="wrap">
<div class="head"><h1>Security summary${companyName ? `: ${escapeHtml(companyName)}` : ''}</h1><p>Collection of ${escapeHtml(collected)}. Exported ${escapeHtml(fmt(now))} from onGuard for Entra ID, IPC Global.</p></div>
<div class="score">onGuard score <strong>${formatScore(num(posture?.score))}</strong>. ${numText(posture?.passCount, '0')} passed, ${numText(posture?.warnCount, '0')} warnings, ${numText(posture?.failCount, '0')} failed, of ${numText(posture?.totalChecks, '0')} checks.</div>
${cats ? `<h2>By area</h2><div class="cats">${cats}</div>` : ''}
${driftHtml}
<h2>Checks</h2>
<table><thead><tr><th>Status</th><th>ID</th><th>Check</th><th>Severity</th><th>Control references</th></tr></thead><tbody>
${rows}
</tbody></table>
<div class="foot">${footer}</div>
</div>
</body>
</html>`;
}

/** Downloads the export as an HTML file. Returns false when there is nothing to export. */
export function exportSecurityReport(posture, { timestamp = null, companyName = '' } = {}) {
  if (!posture) return false;
  const html = securityExportHtml(posture, { timestamp, companyName });
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `security-summary-${new Date().toISOString().slice(0, 10)}.html`;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return true;
}
